import { useState, useCallback } from "react";
import { Gift, RefreshCcw, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const CARD_PRIZES = [
  { text: "10€ OFFERTS", isWin: true },
  { text: "PERDU", isWin: false },
  { text: "CADEAU", isWin: true },
  { text: "PERDU", isWin: false },
  { text: "20% OFF", isWin: true },
  { text: "PERDU", isWin: false },
];

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const FlipCard = ({ prize, index, isFlipped, onFlip, disabled }) => {
  return (
    <button
      onClick={() => onFlip(index)}
      disabled={disabled || isFlipped}
      className="group perspective-500 w-full aspect-[3/4] cursor-pointer disabled:cursor-default"
      data-testid={`instant-card-${index}`}
    >
      <div
        className={`relative w-full h-full transition-transform duration-700 preserve-3d ${
          isFlipped ? "rotate-y-180" : ""
        }`}
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          transition: "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Front - Mystery card */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-lg backface-hidden"
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className="w-full h-full bg-gradient-to-br from-[#1D3557] to-[#2A4A6D] flex flex-col items-center justify-center p-4 border-2 border-[#1D3557] rounded-2xl group-hover:border-[#8C9D78] transition-colors">
            {/* Pattern */}
            <div className="absolute inset-0 opacity-10">
              {Array.from({ length: 12 }).map((_, i) => (
                <Star
                  key={i}
                  className="absolute text-white"
                  style={{
                    width: 12 + Math.random() * 8,
                    height: 12 + Math.random() * 8,
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    opacity: 0.3 + Math.random() * 0.5,
                  }}
                />
              ))}
            </div>
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-full bg-[#8C9D78]/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Gift className="w-8 h-8 text-[#8C9D78]" />
              </div>
              <p className="text-white/80 font-semibold text-sm">CLIQUEZ</p>
              <p className="text-white/50 text-xs">pour découvrir</p>
            </div>
          </div>
        </div>

        {/* Back - Prize */}
        <div
          className="absolute inset-0 rounded-2xl overflow-hidden shadow-lg backface-hidden"
          style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
        >
          <div
            className={`w-full h-full flex flex-col items-center justify-center p-4 border-2 rounded-2xl ${
              prize.isWin
                ? "bg-gradient-to-br from-[#8C9D78] to-[#6B7C5A] border-[#8C9D78]"
                : "bg-gradient-to-br from-stone-100 to-stone-200 border-stone-300"
            }`}
          >
            {prize.isWin ? (
              <>
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-3">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <p className="text-white font-bold text-lg">{prize.text}</p>
                <p className="text-white/70 text-xs mt-1">Bravo !</p>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-stone-300 flex items-center justify-center mb-3">
                  <span className="text-stone-500 text-2xl font-bold">X</span>
                </div>
                <p className="text-stone-500 font-bold text-lg">PERDU</p>
                <p className="text-stone-400 text-xs mt-1">Pas cette fois</p>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const InstantWin = () => {
  const [cards, setCards] = useState(() => shuffle(CARD_PRIZES));
  const [flipped, setFlipped] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(2);
  const [wonPrize, setWonPrize] = useState(null);

  const handleFlip = useCallback(
    (index) => {
      if (gameOver || flipped.includes(index) || attemptsLeft <= 0) return;

      const newFlipped = [...flipped, index];
      setFlipped(newFlipped);
      const newAttempts = attemptsLeft - 1;
      setAttemptsLeft(newAttempts);

      const card = cards[index];
      if (card.isWin) {
        setWonPrize(card.text);
        setGameOver(true);
        // Reveal all after a win
        setTimeout(() => {
          setFlipped(cards.map((_, i) => i));
        }, 1000);
      } else if (newAttempts <= 0) {
        setGameOver(true);
        setTimeout(() => {
          setFlipped(cards.map((_, i) => i));
        }, 800);
      }
    },
    [gameOver, flipped, attemptsLeft, cards]
  );

  const reset = () => {
    setCards(shuffle(CARD_PRIZES));
    setFlipped([]);
    setGameOver(false);
    setAttemptsLeft(2);
    setWonPrize(null);
  };

  return (
    <div className="text-center" data-testid="instant-win">
      {/* Header info */}
      <div className="flex items-center justify-center gap-6 mb-6">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#1D3557]/10 rounded-full">
          <Gift className="w-4 h-4 text-[#1D3557]" />
          <span className="text-sm font-semibold text-[#1D3557]">
            {attemptsLeft} essai{attemptsLeft > 1 ? "s" : ""} restant{attemptsLeft > 1 ? "s" : ""}
          </span>
        </div>
        <div className="text-sm text-stone-500">
          {flipped.length}/{cards.length} cartes révélées
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-3 gap-3 max-w-md mx-auto mb-6">
        {cards.map((card, i) => (
          <FlipCard
            key={i}
            prize={card}
            index={i}
            isFlipped={flipped.includes(i)}
            onFlip={handleFlip}
            disabled={gameOver}
          />
        ))}
      </div>

      {/* Result */}
      {gameOver && (
        <div
          className={`mb-5 p-5 rounded-2xl border-2 animate-in fade-in slide-in-from-bottom-4 duration-500 ${
            wonPrize
              ? "bg-gradient-to-r from-[#8C9D78]/10 to-[#C65D47]/10 border-[#8C9D78]/30"
              : "bg-stone-50 border-stone-200"
          }`}
          data-testid="instant-win-result"
        >
          {wonPrize ? (
            <div className="flex items-center justify-center gap-3">
              <Sparkles className="w-6 h-6 text-[#C65D47]" />
              <p className="font-bold text-xl text-[#1D3557]">
                Gagné : <span className="text-[#C65D47]">{wonPrize}</span>
              </p>
            </div>
          ) : (
            <p className="font-bold text-lg text-stone-500">Pas de chance ! Réessayez !</p>
          )}
        </div>
      )}

      <Button
        onClick={reset}
        variant={gameOver ? "default" : "outline"}
        className={`rounded-full px-8 h-10 ${
          gameOver ? "bg-[#C65D47] hover:bg-[#A84A38] text-white" : "border-[#8C9D78]/30 hover:bg-[#8C9D78]/10"
        }`}
        data-testid="instant-win-reset-btn"
      >
        <RefreshCcw className="w-4 h-4 mr-2" />
        Nouvelle partie
      </Button>
    </div>
  );
};

export default InstantWin;
